import { HUD, HUD_MISSION } from '../../config/tuning';
import type { HudPainter } from '../HudLayout';
import type { HudFrame } from '../HudFrame';

/**
 * Objectives and radio traffic (SPEC §3.10.1, §3.14 subtitles).
 *
 * Two small blocks, both deliberately quiet. The objective list sits top-left in the same thin
 * cyan as the rest of the instrument, one line each, marked rather than coloured — this is a
 * diegetic display, and a green tick would be the "modern game UI vocabulary" SPEC §3.4 rules out
 * in as many words.
 *
 * Radio text is centred low, above the RWR ring, because it is a subtitle: the accessibility set
 * requires every radio call to be readable without audio (§3.14), so the text channel is the
 * primary one and the voice is the redundancy, not the other way round.
 */

/** Objective marks. Shape carries the state so colour is never the only channel (§3.14). */
const MARKS: Record<string, string> = {
  pending: '·',
  complete: '+',
  failed: '×',
};

export function drawMissionPanel(painter: HudPainter, frame: HudFrame): void {
  const layout = painter.layout;
  const margin = layout.px(HUD.marginPx);
  const line = layout.fontPx * 1.4;

  // --- Objectives, top left, below the heading tape's left edge.
  let y = margin * 2.2;
  if (frame.objectives.length > 0) {
    painter.text('TASKING', margin, y, 'left');
    y += line;
    for (let i = 0; i < frame.objectives.length; i += 1) {
      const objective = frame.objectives[i];
      const mark = MARKS[objective.state] ?? '·';
      const colour = objective.state === 'failed' ? HUD.dangerColour : undefined;
      // Secondary objectives are bracketed rather than dimmed: a dimmed line reads as disabled.
      const text = objective.primary
        ? `${mark} ${objective.description}`
        : `${mark} (${objective.description})`;
      painter.text(text, margin, y, 'left', colour);
      y += line;
    }
  }

  // --- Radio subtitle, centred, above the RWR ring.
  if (frame.radioText !== '') {
    const centreX = layout.widthPx * 0.5;
    const subtitleY = layout.heightPx - layout.px(HUD_MISSION.radioSubtitleBottomPx);
    painter.text(`${frame.radioSpeaker}:`, centreX, subtitleY - line, 'centre');
    painter.text(frame.radioText, centreX, subtitleY, 'centre');
  }
}
